'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Field, Input, useKiaoConfirm, toast } from '@kiao-voice/ui-kit'
import { getSeatPaymentStatus, inviteMember, updateTeamSeats } from './api-client'
import {
  clearPendingInvite,
  makeInviteAttemptId,
  persistPendingInvite,
  readPendingInvite,
} from './pending-team-invite'
import { computeSeatProration, formatTwd, type SubscriptionCycle } from './seat-adjustment'
import { JoinLinkPanel } from './join-link-panel'

/** 滿席自動加開「成功」之後邀請才失敗——席位已付費存在，重試只會送邀請
 *  不會再扣款。submit 以此類別區分，顯示專屬的可行動錯誤文案。 */
class InviteAfterSeatAddError extends Error {
  constructor(cause: unknown) {
    super('invite failed after the seat was provisioned')
    this.name = 'InviteAfterSeatAddError'
    this.cause = cause
  }
}

export interface AddMemberSectionProps {
  organizationId: number
  ownerEmail: string | null
  currentSeats: number
  seatsAvailable: number
  perSeatPriceTwd: number | null
  cycle: SubscriptionCycle
  periodStart: string | null
  periodEnd: string | null
  getTokenFn: () => string | null
  redirect?: (url: string) => void
  onDone: () => void
  onError: (cause: unknown) => void
}

/**
 * `02 新增成員`：兩種「方式」用髮絲線分層——方式一寄 email 邀請（本節唯一的
 * primary 動作），方式二分享加入連結。
 *
 * 邀請流程本身（確認、滿席自動加開一席、3DS 轉跳、pending invite 續傳）是原
 * `InviteMember` 的邏輯原封搬過來，只換外觀；唯一的行為變更是滿席確認文案改帶
 * 精確金額，用的是與席次調整同一支 `seat-adjustment.ts`。
 */
export function AddMemberSection(props: AddMemberSectionProps) {
  const { t } = useTranslation('web')
  return (
    <div className="flex flex-col gap-[var(--gap-md)]">
      <div>
        <h3 className="text-base font-semibold">{t('team.wayInviteTitle')}</h3>
        <p className="mt-[var(--gap-xs)] text-xs text-muted">{t('team.inviteDesc')}</p>
        <InviteMember {...props} />
      </div>
      <div className="border-t border-border pt-[var(--gap-md)]">
        <h3 className="text-base font-semibold">{t('team.wayLinkTitle')}</h3>
        <p className="mt-[var(--gap-xs)] text-xs text-muted">{t('team.joinLinkDescription')}</p>
        <JoinLinkPanel key={props.organizationId} organizationId={props.organizationId} />
      </div>
    </div>
  )
}

function InviteMember({
  organizationId,
  ownerEmail,
  currentSeats,
  seatsAvailable,
  perSeatPriceTwd,
  cycle,
  periodStart,
  periodEnd,
  getTokenFn,
  redirect,
  onDone,
  onError,
}: AddMemberSectionProps) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [resumeTick, setResumeTick] = useState(0)
  const resumedPendingKey = useRef<string | null>(null)

  const sendInvite = useCallback(
    async (value: string, attemptId: string) => {
      await inviteMember(organizationId, value, attemptId)
      toast.success(t('team.inviteSent', { email: value }))
    },
    [organizationId, t]
  )

  // Resume an exact invite attempt. A free seat is not proof that this attempt's
  // 3DS payment landed, so trade-backed attempts query their own durable
  // finalization marker before sending. A response-lost invite has no trade id;
  // replaying its stable attempt id is safe even when the existing invited row
  // already consumes the last seat.
  useEffect(() => {
    let disposed = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    const pending = readPendingInvite(organizationId, ownerEmail)
    if (!pending) return
    const token = getTokenFn()
    if (!token) return
    const attemptKey = `${organizationId}:${ownerEmail}:${pending.tradeId ?? 'none'}:${pending.attemptId}:${pending.email}`
    if (resumedPendingKey.current === attemptKey) return
    resumedPendingKey.current = attemptKey
    const resume = async () => {
      if (pending.tradeId !== null) {
        const payment = await getSeatPaymentStatus(pending.tradeId)
        if (payment.status === 'failed') {
          clearPendingInvite(organizationId, ownerEmail!)
          return
        }
        if (payment.status !== 'finalized') {
          retryTimer = setTimeout(() => {
            if (disposed) return
            resumedPendingKey.current = null
            setResumeTick((tick) => tick + 1)
          }, 2_000)
          return
        }
      }
      await sendInvite(pending.email, pending.attemptId)
      clearPendingInvite(organizationId, ownerEmail!)
      onDone()
    }
    void resume().catch((cause) => {
      reportHandledException(cause, { source: 'features/team/add-member-section.tsx' })
      // Keep the stable attempt in storage. Reloading retries the same token
      // and the same Resend idempotency key, never a second invitation.
      onError(cause)
    })
    return () => {
      disposed = true
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [organizationId, ownerEmail, getTokenFn, sendInvite, onDone, onError, resumeTick])

  /** Provision one more seat (may redirect for 3DS) then invite. */
  const provisionAndInvite = useCallback(
    async (value: string, attemptId: string) => {
      const result = await updateTeamSeats(currentSeats + 1)
      if (result.requires3ds && result.paymentUrl) {
        // 3DS redirects away; the seat lands via the notify webhook. Stash
        // the invite so the resume effect finishes it on return.
        persistPendingInvite({
          email: value,
          organizationId,
          ownerEmail,
          attemptId,
          tradeId: result.tradeId,
        })
        if (redirect) redirect(result.paymentUrl)
        else if (typeof window !== 'undefined') window.location.href = result.paymentUrl
        return
      }
      persistPendingInvite({ email: value, organizationId, ownerEmail, attemptId })
      try {
        await sendInvite(value, attemptId)
        if (ownerEmail) clearPendingInvite(organizationId, ownerEmail)
      } catch (cause) {
        // 席位已加開成功、邀請才失敗：把邀請留在 stash（席次重新整理後 resume
        // effect 會免費補送），並以可辨識錯誤讓 UI 顯示「重試不會重複扣款」。
        throw new InviteAfterSeatAddError(cause)
      }
    },
    [currentSeats, organizationId, ownerEmail, redirect, sendInvite]
  )

  /** 滿席確認的精確金額——與席次調整預覽同一支純函式，不另外算一次。 */
  const fullSeatConfirmMessage = useCallback(
    (value: string) => {
      const amount = computeSeatProration(1, {
        perSeatPriceTwd,
        periodStart,
        periodEnd,
        now: Date.now(),
      })
      if (!amount) return t('team.addSeatConfirmNoAmount', { email: value })
      return t(cycle === 'yearly' ? 'team.addSeatConfirmYearly' : 'team.addSeatConfirmMonthly', {
          email: value,
          full: formatTwd(amount.full),
          charge: formatTwd(amount.charge),
      })
    },
    [cycle, perSeatPriceTwd, periodEnd, periodStart, t]
  )

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const token = getTokenFn()
      const value = email.trim()
      setError(null)
      if (!value || value.indexOf('@') === -1) {
        setError(t('team.errInvalidEmail'))
        return
      }
      if (!token || busy) return

      // Decide upfront on the server's seat truth: a free seat costs nothing,
      // a full roster auto-provisions one prorated seat. Either way the owner
      // confirms before anything happens.
      const full = seatsAvailable <= 0
      const ok = await confirm(
        full
          ? {
              title: t('team.addSeatConfirmFullTitle'),
              message: fullSeatConfirmMessage(value),
              confirmText: t('team.addSeatConfirmCta'),
              cancelText: t('common:cancel'),
            }
          : {
              title: t('team.inviteConfirmTitle'),
              message: t('team.inviteConfirmBody', { email: value }),
              confirmText: t('team.inviteButton'),
              cancelText: t('common:cancel'),
            }
      )
      if (!ok) return

      setBusy(true)
      const attemptId = makeInviteAttemptId()
      try {
        if (full) {
          await provisionAndInvite(value, attemptId)
        } else {
          persistPendingInvite({ email: value, organizationId, ownerEmail, attemptId })
          await sendInvite(value, attemptId)
          if (ownerEmail) clearPendingInvite(organizationId, ownerEmail)
        }
        setEmail('')
        onDone()
      } catch (cause) {
        reportHandledException(cause, { source: 'features/team/add-member-section.tsx' })
        if (cause instanceof InviteAfterSeatAddError) {
          // 席位已扣款成功——給具體可行動的錯誤並刷新席次（stash 會自動補送）。
          setError(t('team.errInviteAfterSeatAdd'))
          onDone()
        } else {
          onError(cause)
        }
      } finally {
        setBusy(false)
      }
    },
    [
      getTokenFn,
      email,
      busy,
      seatsAvailable,
      fullSeatConfirmMessage,
      confirm,
      t,
      provisionAndInvite,
      organizationId,
      ownerEmail,
      sendInvite,
      onDone,
      onError,
    ]
  )

  return (
    <>
      <form
        onSubmit={submit}
        className="mt-[var(--gap-sm)] flex max-w-[520px] flex-wrap items-end gap-[var(--gap-sm)]"
      >
        <Field
          label={t('team.inviteEmailField')}
          htmlFor="invite-email"
          className="min-w-[220px] flex-1"
        >
          <Input
            id="invite-email"
            type="email"
            value={email}
            disabled={busy}
            placeholder={t('team.inviteEmailPlaceholder')}
            data-testid="invite-email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="primary" size="control" disabled={busy}>
          {busy ? t('team.inviting') : t('team.inviteButton')}
        </Button>
      </form>
      {error ? <Alert className="mt-[var(--gap-sm)]">{error}</Alert> : null}
    </>
  )
}
